import { coreClient } from '@/api/coreclient'
import type {
  OrganizationAttributes,
  ReportVisitHistoryEntry,
  UserAttributes,
  UserUpdatePayload,
  ScheduledReport
} from 'common'

export default {
  async resendVerifyEmail() {
    const client = await coreClient()
    return client.user
      .resendVerifyEmail()
      .then(() => {
        return true
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async updateUserData(payload: UserUpdatePayload) {
    const client = await coreClient()
    return client.user
      .updateUserData(payload)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async removeFromOrganization(userId: string, transferUserId?: string) {
    const client = await coreClient()
    return client.user
      .removeFromOrganization({ user_id: userId, transfer_user_id: transferUserId })
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getOrganization() {
    const client = await coreClient()
    return client.user
      .getOrganization()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getOrganizationTagsWithCount() {
    const client = await coreClient()
    return client.user
      .getOrganizationTagsWithCount()
      .then((response) => response.data)
      .catch((e) => {
        console.error(e)
        return []
      })
  },
  async updateOrganization(id: number, org: Omit<OrganizationAttributes, 'id'>) {
    const client = await coreClient()
    return client.user
      .patchOrganization(id, org)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async invitePeople(emails: string[]) {
    const client = await coreClient()
    return client.user
      .invitePeople({ emails })
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async seatUpdateInvoice(seats: number) {
    const client = await coreClient()
    return client.user
      .seatUpdateInvoice(seats)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return {
          amount_due: 0,
          total: 0,
          discount: null,
          subtotal: 0
        }
      })
  },
  async resendInviteEmail(email: string) {
    const client = await coreClient()
    return client.user
      .resendInviteEmail(email)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getInvitation() {
    const client = await coreClient()
    return client.user
      .getInvitation()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async acceptInvitation() {
    const client = await coreClient()
    return client.user
      .acceptInvitation()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async denyInvitation() {
    const client = await coreClient()
    return client.user
      .denyInvitation()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getPeopleInOrgOrShared() {
    const client = await coreClient()
    return client.user
      .getPeopleInOrgOrShared()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return []
      })
  },
  async searchPeople(q: string, limit: number = 8) {
    const client = await coreClient()
    return client.user
      .searchPeople(q, limit)
      .then((response) => {
        return response.data || []
      })
      .catch((e) => {
        console.error(e)
        return []
      })
  },
  async getReportHistory() {
    const client = await coreClient()
    return client.user
      .getReportHistory()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as ReportVisitHistoryEntry[]
      })
  },
  async searchGroups(q: string, limit: number = 8) {
    const client = await coreClient()
    return client.user
      .searchGroups(q, limit)
      .then((response) => {
        return (response.data || []) as { id: number; name: string }[]
      })
      .catch((e) => {
        console.error(e)
        return [] as { id: number; name: string }[]
      })
  },
  async reportIdsByUser(userId: string) {
    const client = await coreClient()
    return client.user
      .reportIdsByUser(userId)
      .then((response) => {
        return response.data || []
      })
      .catch((e) => {
        console.error(e)
        return [] as number[]
      })
  },
  async listOrganizationReportAccesses(
    page: number,
    limit: number,
    search?: string,
    sortBy?: string,
    descending?: boolean
  ) {
    const client = await coreClient()
    return client.user
      .listOrganizationReportAccesses(page, limit, search, sortBy, descending)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return { items: [] as (UserAttributes & { report_ids: number[] })[], total: 0 }
      })
  },
  async listOrganizationMembers(
    filters?: {
      search?: string
      sort_by?: string
      descending?: boolean
      group_ids?: (number | null)[]
    },
    pagination?: {
      page?: number
      limit?: number
    }
  ) {
    const client = await coreClient()
    const groupIds = filters?.group_ids?.join(',')
    return client.user
      .listOrganizationMembers(
        filters?.search,
        filters?.sort_by,
        filters?.descending,
        groupIds,
        pagination?.page,
        pagination?.limit
      )
      .then((response) => {
        return response as unknown as { items: UserAttributes[]; total: number }
      })
      .catch((e) => {
        console.error(e)
        return { items: [] as UserAttributes[], total: 0 }
      })
  },
  async getSchedules() {
    const client = await coreClient()
    return client.user
      .getSchedules()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as ScheduledReport[]
      })
  },
  async unsubscribeFromSchedule(id: number) {
    const client = await coreClient()
    return client.user
      .unsubscribeFromSchedule(id)
      .then(() => {
        return true
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  }
}
